import React, { useState, useEffect } from 'react';
import { RotateCcw, Ruler, Weight, ArrowLeftRight } from 'lucide-react';
import { UnitConverter } from '@/lib/unit-converter';
import { formatNumber } from '@/lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import Input from '@/components/ui/input';
import Button from '@/components/ui/button';

export default function UnitConverterComponent() {
  const [lengthInputs, setLengthInputs] = useState({
    cm: '',
    inches: ''
  });

  const [weightInputs, setWeightInputs] = useState({
    grams: '',
    kilograms: '',
    pounds: '',
    ounces: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // 长度转换
  useEffect(() => {
    const timer = setTimeout(() => {
      if (lengthInputs.cm && UnitConverter.validateInput(lengthInputs.cm)) {
        const cmValue = parseFloat(lengthInputs.cm);
        const result = UnitConverter.convertLength(cmValue, 'cm', 'in');
        setLengthInputs(prev => ({
          ...prev,
          inches: UnitConverter.formatValue(result.to.value)
        }));
      } else if (lengthInputs.inches && UnitConverter.validateInput(lengthInputs.inches)) {
        const inchesValue = parseFloat(lengthInputs.inches);
        const result = UnitConverter.convertLength(inchesValue, 'in', 'cm');
        setLengthInputs(prev => ({
          ...prev,
          cm: UnitConverter.formatValue(result.to.value)
        }));
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [lengthInputs.cm, lengthInputs.inches]);

  // 重量转换
  useEffect(() => {
    const timer = setTimeout(() => {
      // 克输入
      if (weightInputs.grams && UnitConverter.validateInput(weightInputs.grams)) {
        const gramsValue = parseFloat(weightInputs.grams);
        const kgResult = UnitConverter.convertWeight(gramsValue, 'g', 'kg');
        const lbResult = UnitConverter.convertWeight(gramsValue, 'g', 'lb');
        const ozResult = UnitConverter.convertWeight(gramsValue, 'g', 'oz');

        setWeightInputs(prev => ({
          ...prev,
          kilograms: UnitConverter.formatValue(kgResult.to.value),
          pounds: UnitConverter.formatValue(lbResult.to.value),
          ounces: UnitConverter.formatValue(ozResult.to.value)
        }));
      }
      // 千克输入
      else if (weightInputs.kilograms && UnitConverter.validateInput(weightInputs.kilograms)) {
        const kgValue = parseFloat(weightInputs.kilograms);
        const results = UnitConverter.convertWeightToMultiple(kgValue, 'kg', ['g', 'lb', 'oz']);

        setWeightInputs(prev => ({
          ...prev,
          grams: UnitConverter.formatValue(results.g),
          pounds: UnitConverter.formatValue(results.lb),
          ounces: UnitConverter.formatValue(results.oz)
        }));
      }
      // 磅输入
      else if (weightInputs.pounds && UnitConverter.validateInput(weightInputs.pounds)) {
        const lbValue = parseFloat(weightInputs.pounds);
        const results = UnitConverter.convertWeightToMultiple(lbValue, 'lb', ['g', 'kg', 'oz']);

        setWeightInputs(prev => ({
          ...prev,
          grams: UnitConverter.formatValue(results.g),
          kilograms: UnitConverter.formatValue(results.kg),
          ounces: UnitConverter.formatValue(results.oz)
        }));
      }
      // 盎司输入
      else if (weightInputs.ounces && UnitConverter.validateInput(weightInputs.ounces)) {
        const ozValue = parseFloat(weightInputs.ounces);
        const results = UnitConverter.convertWeightToMultiple(ozValue, 'oz', ['g', 'kg', 'lb']);

        setWeightInputs(prev => ({
          ...prev,
          grams: UnitConverter.formatValue(results.g),
          kilograms: UnitConverter.formatValue(results.kg),
          pounds: UnitConverter.formatValue(results.lb)
        }));
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [weightInputs.grams, weightInputs.kilograms, weightInputs.pounds, weightInputs.ounces]);

  const handleLengthChange = (unit: 'cm' | 'inches', value: string) => {
    if (value === '' || UnitConverter.validateInput(value)) {
      setLengthInputs(prev => ({
        ...prev,
        [unit]: value,
        [unit === 'cm' ? 'inches' : 'cm']: '' // 清空对应的字段
      }));
    }
  };

  const handleWeightChange = (unit: keyof typeof weightInputs, value: string) => {
    if (value === '' || UnitConverter.validateInput(value)) {
      setWeightInputs(prev => ({
        ...prev,
        [unit]: value
      }));
    }
  };

  const clearAll = () => {
    setLengthInputs({ cm: '', inches: '' });
    setWeightInputs({ grams: '', kilograms: '', pounds: '', ounces: '' });
    setErrors({});
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <RotateCcw className="w-5 h-5 text-amazon-blue" />
            <span>单位换算器</span>
          </CardTitle>
          <CardDescription>
            公制和英制单位之间的实时转换，支持长度和重量单位
          </CardDescription>
        </CardHeader>
      </Card>

      {/* 长度换算 */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Ruler className="w-5 h-5 text-blue-600" />
            <span>长度单位换算</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-2">
              <Input
                label="厘米 (cm)"
                type="number"
                step="0.01"
                placeholder="输入厘米"
                value={lengthInputs.cm}
                onChange={(e) => handleLengthChange('cm', e.target.value)}
                error={errors.cm}
              />
              <p className="text-xs text-gray-500">
                1 cm = 0.393701 inches
              </p>
            </div>

            <div className="flex items-center justify-center">
              <ArrowLeftRight className="w-6 h-6 text-gray-400" />
            </div>

            <div className="space-y-2">
              <Input
                label="英寸 (in)"
                type="number"
                step="0.01"
                placeholder="输入英寸"
                value={lengthInputs.inches}
                onChange={(e) => handleLengthChange('inches', e.target.value)}
                error={errors.inches}
              />
              <p className="text-xs text-gray-500">
                1 in = 2.54 cm
              </p>
            </div>
          </div>

          {lengthInputs.cm && lengthInputs.inches && (
            <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="text-center">
                <div className="text-lg font-semibold text-blue-800">
                  {lengthInputs.cm} cm = {lengthInputs.inches} inches
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* 重量换算 */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Weight className="w-5 h-5 text-green-600" />
            <span>重量单位换算</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Input
                label="克 (g)"
                type="number"
                step="0.01"
                placeholder="输入克"
                value={weightInputs.grams}
                onChange={(e) => handleWeightChange('grams', e.target.value)}
              />
              <p className="text-xs text-gray-500">基础单位</p>
            </div>

            <div className="space-y-2">
              <Input
                label="千克 (kg)"
                type="number"
                step="0.001"
                placeholder="输入千克"
                value={weightInputs.kilograms}
                onChange={(e) => handleWeightChange('kilograms', e.target.value)}
              />
              <p className="text-xs text-gray-500">1 kg = 1000 g</p>
            </div>

            <div className="space-y-2">
              <Input
                label="磅 (lb)"
                type="number"
                step="0.01"
                placeholder="输入磅"
                value={weightInputs.pounds}
                onChange={(e) => handleWeightChange('pounds', e.target.value)}
              />
              <p className="text-xs text-gray-500">1 lb = 453.592 g</p>
            </div>

            <div className="space-y-2">
              <Input
                label="盎司 (oz)"
                type="number"
                step="0.01"
                placeholder="输入盎司"
                value={weightInputs.ounces}
                onChange={(e) => handleWeightChange('ounces', e.target.value)}
              />
              <p className="text-xs text-gray-500">1 oz = 28.3495 g</p>
            </div>
          </div>

          {/* 重量换算结果 */}
          {(weightInputs.grams || weightInputs.kilograms || weightInputs.pounds || weightInputs.ounces) && (
            <div className="mt-6 space-y-3">
              <h4 className="font-semibold text-gray-700">换算结果</h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {weightInputs.grams && (
                  <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg border border-green-200">
                    <span className="text-green-700 font-medium">克 (g)</span>
                    <span className="text-green-800 font-semibold">{weightInputs.grams} g</span>
                  </div>
                )}
                {weightInputs.kilograms && (
                  <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg border border-green-200">
                    <span className="text-green-700 font-medium">千克 (kg)</span>
                    <span className="text-green-800 font-semibold">{weightInputs.kilograms} kg</span>
                  </div>
                )}
                {weightInputs.pounds && (
                  <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg border border-green-200">
                    <span className="text-green-700 font-medium">磅 (lb)</span>
                    <span className="text-green-800 font-semibold">{weightInputs.pounds} lb</span>
                  </div>
                )}
                {weightInputs.ounces && (
                  <div className="flex justify-between items-center p-3 bg-green-50 rounded-lg border border-green-200">
                    <span className="text-green-700 font-medium">盎司 (oz)</span>
                    <span className="text-green-800 font-semibold">{weightInputs.ounces} oz</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* 快捷换算参考 */}
      <Card>
        <CardHeader>
          <CardTitle>常用换算参考</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h4 className="font-semibold text-gray-700 mb-3">长度换算</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between items-center py-1">
                  <span className="text-gray-600">1 inch</span>
                  <span className="font-medium">2.54 cm</span>
                </div>
                <div className="flex justify-between items-center py-1">
                  <span className="text-gray-600">1 foot</span>
                  <span className="font-medium">12 inches / 30.48 cm</span>
                </div>
                <div className="flex justify-between items-center py-1">
                  <span className="text-gray-600">1 cm</span>
                  <span className="font-medium">0.393701 inches</span>
                </div>
                <div className="flex justify-between items-center py-1">
                  <span className="text-gray-600">1 m</span>
                  <span className="font-medium">39.3701 inches</span>
                </div>
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-gray-700 mb-3">重量换算</h4>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between items-center py-1">
                  <span className="text-gray-600">1 ounce</span>
                  <span className="font-medium">28.3495 g</span>
                </div>
                <div className="flex justify-between items-center py-1">
                  <span className="text-gray-600">1 pound</span>
                  <span className="font-medium">16 oz / 453.592 g</span>
                </div>
                <div className="flex justify-between items-center py-1">
                  <span className="text-gray-600">1 kg</span>
                  <span className="font-medium">1000 g / 2.20462 lb</span>
                </div>
                <div className="flex justify-between items-center py-1">
                  <span className="text-gray-600">1 g</span>
                  <span className="font-medium">0.00220462 lb</span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* 清空按钮 */}
      <div className="text-center">
        <Button onClick={clearAll} variant="secondary" size="lg">
          清空所有输入
        </Button>
      </div>
    </div>
  );
}